import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, Trash2, FolderOpen, Plus, X, FileText, Loader2,
  CheckCircle, AlertCircle, Clock, Database, ChevronRight,
  LogOut, Coins, ArrowLeft, RefreshCw, Image, Check,
} from 'lucide-react';
import FeatureGate from '../components/FeatureGate';
import AppHeader from '../components/AppHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Predefined categories (must match backend PREDEFINED_CATEGORIES)
const PREDEFINED_CATEGORIES = ['HR', 'Legal', 'Finance', 'Marketing', 'Tech', 'General'];

const CATEGORY_COLORS = {
  HR: { light: { bg: '#f3e8ff', text: '#7e22ce', border: '#e9d5ff' }, dark: { bg: 'rgba(147,51,234,0.2)', text: '#d8b4fe', border: '#9333ea' } },
  Legal: { light: { bg: '#dbeafe', text: '#1d4ed8', border: '#bfdbfe' }, dark: { bg: 'rgba(59,130,246,0.2)', text: '#93c5fd', border: '#3b82f6' } },
  Finance: { light: { bg: '#dcfce7', text: '#15803d', border: '#bbf7d0' }, dark: { bg: 'rgba(34,197,94,0.2)', text: '#86efac', border: '#22c55e' } },
  Marketing: { light: { bg: '#ffedd5', text: '#c2410c', border: '#fed7aa' }, dark: { bg: 'rgba(249,115,22,0.2)', text: '#fdba74', border: '#f97316' } },
  Tech: { light: { bg: '#cffafe', text: '#0e7490', border: '#a5f3fc' }, dark: { bg: 'rgba(6,182,212,0.2)', text: '#67e8f9', border: '#06b6d4' } },
  General: { light: { bg: '#f1f5f9', text: '#334155', border: '#e2e8f0' }, dark: { bg: 'rgba(71,85,105,0.4)', text: '#cbd5e1', border: '#475569' } },
};

function getCategoryColor(name, isDark) {
  const themeObj = CATEGORY_COLORS[name] || { light: { bg: '#fce7f3', text: '#be185d', border: '#fbcfe8' }, dark: { bg: 'rgba(236,72,153,0.2)', text: '#f9a8d4', border: '#ec4899' } };
  const t = isDark ? themeObj.dark : themeObj.light;
  return { backgroundColor: t.bg, color: t.text, borderColor: t.border };
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusBadge({ status, chunkCount }) {
  if (status === 'indexing') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 font-medium">
        <Loader2 className="w-3 h-3 animate-spin" /> Indexing…
      </span>
    );
  }
  if (status === 'indexed') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400 font-medium">
        <CheckCircle className="w-3 h-3" /> Ready · {chunkCount} chunks
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-red-500 dark:text-red-400 font-medium">
      <AlertCircle className="w-3 h-3" /> Failed
    </span>
  );
}

export default function KnowledgeBase() {
  const { user, token, logout, isAuthenticated } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Theme-aware style tokens (bypasses global CSS !important overrides)
  const bg        = isDark ? '#0a0f1e' : '#f8fafc';
  const cardBg    = isDark ? '#0f1729' : '#ffffff';
  const border    = isDark ? '#1e2d52' : '#e2e8f0';
  const textPrim  = isDark ? '#f0f6fc' : '#0f172a';
  const textMuted = isDark ? '#8b949e' : '#64748b';
  const inputBg   = isDark ? '#0f1729' : '#ffffff';
  const hoverBg   = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('General');
  const [files, setFiles] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);

  // Create category
  const [showNewCatInput, setShowNewCatInput] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [creatingCat, setCreatingCat] = useState(false);

  // Drag-over state for upload zone
  const [isDragOver, setIsDragOver] = useState(false);

  // Google Drive import state
  const [driveConnected, setDriveConnected] = useState(false);
  const [connectingDrive, setConnectingDrive] = useState(false);
  const [showDrivePicker, setShowDrivePicker] = useState(false);
  const [driveFiles, setDriveFiles] = useState([]);
  const [driveSearch, setDriveSearch] = useState('');
  const [driveLoading, setDriveLoading] = useState(false);
  const [driveImporting, setDriveImporting] = useState(null);

  const authHeader = { Authorization: `Bearer ${token}` };

  // Check feature access — skip all API calls if user cannot access KB
  const hasKBAccess =
    user?.plan === 'enterprise_pro' ||
    (user?.feature_access || []).includes('knowledge_base');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (hasKBAccess) {
      fetchCategories();
    } else {
      setLoadingCats(false);
    }
  }, [isAuthenticated, navigate, hasKBAccess]);

  useEffect(() => {
    if (hasKBAccess && selectedCategory) fetchFiles(selectedCategory);
  }, [selectedCategory, hasKBAccess]);

  // Check if Google Drive is connected + handle OAuth callback redirect
  useEffect(() => {
    if (!isAuthenticated || !token || !hasKBAccess) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('connector') === 'gdrive_connected') {
      setDriveConnected(true);
      toast.success('Google Drive connected!');
      const url = new URL(window.location.href);
      url.searchParams.delete('connector');
      window.history.replaceState({}, '', url);
    }
    axios.get(`${API}/connectors/status`, { headers: authHeader })
      .then(res => {
        const active = res.data?.connectors || [];
        setDriveConnected(active.includes('google_drive'));
      }).catch(() => { });
  }, [isAuthenticated, token, hasKBAccess]);

  // const connectDrive = () => {
  //   const oauthUrl = `${process.env.REACT_APP_BACKEND_URL}/api/connectors/oauth/gdrive?token=${encodeURIComponent(token)}`;
  //   setConnectingDrive(true);
  //   const popup = window.open(oauthUrl, 'connect_gdrive', 'width=600,height=700');

  //   const onMessage = async (event) => {
  //     if (event.data?.type === 'oauth_complete' && event.data?.connector === 'gdrive_connected') {
  //       window.removeEventListener('message', onMessage);
  //       clearInterval(poll);
  //       setConnectingDrive(false);
  //       setDriveConnected(true);
  //       toast.success('Google Drive connected!');
  //       if (popup && !popup.closed) popup.close();
  //     }
  //   };
  //   window.addEventListener('message', onMessage);

  //   const poll = setInterval(async () => {
  //     if (!popup || popup.closed) {
  //       clearInterval(poll);
  //       window.removeEventListener('message', onMessage);
  //       setConnectingDrive(false);
  //       try {
  //         const res = await axios.get(`${API}/connectors/status`, { headers: authHeader });
  //         const active = res.data?.connectors || [];
  //         const connected = active.includes('google_drive');
  //         setDriveConnected(connected);
  //         if (connected) toast.success('Google Drive connected!');
  //       } catch { /* ignore */ }
  //     }
  //   }, 1000);
  // };
  const connectDrive = async () => {
    setConnectingDrive(true);
    try {
        // 1. Get ticket from authenticated start endpoint
        const { data } = await axios.get(
            `${API}/connectors/oauth/gdrive/start`,
            { headers: authHeader }
        );

        // 2. Open popup with the oauth_url returned directly from backend
        const popup = window.open(data.oauth_url, 'connect_gdrive', 'width=600,height=700');

        const onMessage = async (event) => {
            if (event.data?.type === 'oauth_complete' && event.data?.connector === 'gdrive_connected') {
                window.removeEventListener('message', onMessage);
                clearInterval(poll);
                setConnectingDrive(false);
                setDriveConnected(true);
                toast.success('Google Drive connected!');
                if (popup && !popup.closed) popup.close();
            }
        };
        window.addEventListener('message', onMessage);

        const poll = setInterval(async () => {
            if (!popup || popup.closed) {
                clearInterval(poll);
                window.removeEventListener('message', onMessage);
                setConnectingDrive(false);
                try {
                    const res = await axios.get(`${API}/connectors/status`, { headers: authHeader });
                    const active = res.data?.connectors || [];
                    const connected = active.includes('google_drive');
                    setDriveConnected(connected);
                    if (connected) toast.success('Google Drive connected!');
                } catch { /* ignore */ }
            }
        }, 1000);

    } catch (err) {
        setConnectingDrive(false);
        toast.error('Failed to start Google Drive connection');
    }
};

  // Fetch Drive files when picker opens or search changes
  useEffect(() => {
    if (!showDrivePicker || !driveConnected || !token) return;
    const fetchDriveFiles = async () => {
      setDriveLoading(true);
      try {
        const params = driveSearch ? `?q=${encodeURIComponent(driveSearch)}` : '';
        const res = await axios.get(`${API}/connectors/gdrive/files${params}`, {
          headers: authHeader,
        });
        setDriveFiles(res.data?.files || []);
      } catch (e) { console.error('Drive files fetch failed', e); }
      setDriveLoading(false);
    };
    const debounce = setTimeout(fetchDriveFiles, driveSearch ? 400 : 0);
    return () => clearTimeout(debounce);
  }, [showDrivePicker, driveSearch, driveConnected, token]);

  const importDriveFile = async (file) => {
    setDriveImporting(file.id);
    try {
      await axios.post(`${API}/connectors/gdrive/import-to-kb`, {
        file_id: file.id,
        category: selectedCategory,
      }, { headers: authHeader });
      toast.success(`"${file.name}" imported to ${selectedCategory} — indexing...`);
      fetchFiles(selectedCategory);
      fetchCategories(true); // preserve current category selection
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to import from Drive');
    }
    setDriveImporting(null);
  };

  const getDriveFileIcon = (mimeType) => {
    if (!mimeType) return <FileText className="w-4 h-4 text-slate-500 dark:text-slate-400" />;
    if (mimeType.includes('image/')) return <Image className="w-4 h-4 text-green-500 dark:text-green-400" />;
    if (mimeType.includes('pdf')) return <FileText className="w-4 h-4 text-red-500 dark:text-red-400" />;
    if (mimeType.includes('document') || mimeType.includes('word')) return <FileText className="w-4 h-4 text-blue-500 dark:text-blue-400" />;
    return <FileText className="w-4 h-4 text-slate-500 dark:text-slate-400" />;
  };

  // Poll files with "indexing" status every 3 s
  useEffect(() => {
    if (!hasKBAccess) return;
    const hasIndexing = files.some(f => f.status === 'indexing');
    if (!hasIndexing) return;
    const timer = setInterval(() => fetchFiles(selectedCategory, /* silent */ true), 3000);
    return () => clearInterval(timer);
  }, [files, selectedCategory]);

  const fetchCategories = async (preserveSelection = false) => {
    try {
      const res = await axios.get(`${API}/kb/categories`, { headers: authHeader });
      const cats = Array.isArray(res.data) ? res.data : [];
      setCategories(cats);
      // Only auto-switch category during initial load (when no category is selected yet).
      // After uploads / deletions, keep the user's current selection so the active
      // category never disappears from the sidebar.
      if (!preserveSelection && cats.length > 0 && !cats.find(c => c.name === selectedCategory)) {
        setSelectedCategory(cats[0].name);
      }
    } catch (err) {
      toast.error('Failed to load knowledge base categories');
    } finally {
      setLoadingCats(false);
    }
  };

  const fetchFiles = async (category, silent = false) => {
    if (!silent) setLoadingFiles(true);
    try {
      const res = await axios.get(`${API}/kb/files?category=${encodeURIComponent(category)}`, {
        headers: authHeader,
      });
      setFiles(Array.isArray(res.data) ? res.data : []);
    } catch {
      if (!silent) toast.error('Failed to load files');
    } finally {
      if (!silent) setLoadingFiles(false);
    }
  };

  const handleFileUpload = async (selectedFile) => {
    if (!selectedFile) return;

    const ext = '.' + selectedFile.name.split('.').pop().toLowerCase();
    const allowed = ['.pdf', '.txt', '.csv', '.json', '.md', '.doc', '.docx', '.xls', '.xlsx'];
    if (!allowed.includes(ext)) {
      toast.error(`File type '${ext}' not supported. Allowed: ${allowed.join(', ')}`);
      return;
    }
    if (selectedFile.size > 20 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 20 MB.');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('category', selectedCategory);

    try {
      await axios.post(`${API}/kb/upload`, formData, {
        headers: { ...authHeader, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`"${selectedFile.name}" uploaded — indexing in background…`);
      fetchFiles(selectedCategory);
      fetchCategories(true); // preserve current category selection
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileInputChange = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFileUpload(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFileUpload(f);
  };

  const handleDeleteFile = async (fileId, filename) => {
    if (!window.confirm(`Remove "${filename}" from the knowledge base?`)) return;
    setDeletingFileId(fileId);
    try {
      await axios.delete(`${API}/kb/files/${fileId}`, { headers: authHeader });
      toast.success(`"${filename}" removed`);
      setFiles(prev => prev.filter(f => f.id !== fileId));
      fetchCategories(true); // preserve current category selection
    } catch {
      toast.error('Failed to delete file');
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleCreateCategory = async () => {
    const name = newCatName.trim();
    if (!name) return;
    if (PREDEFINED_CATEGORIES.includes(name)) {
      toast.error(`"${name}" is a predefined category — select it from the list`);
      return;
    }
    setCreatingCat(true);
    try {
      await axios.post(`${API}/kb/categories`, { name }, { headers: authHeader });
      toast.success(`Category "${name}" created`);
      setNewCatName('');
      setShowNewCatInput(false);
      await fetchCategories();
      setSelectedCategory(name);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create category');
    } finally {
      setCreatingCat(false);
    }
  };

  const handleDeleteCategory = async (catName) => {
    if (!window.confirm(`Delete category "${catName}" and all its files?`)) return;
    try {
      await axios.delete(`${API}/kb/categories/${encodeURIComponent(catName)}`, {
        headers: authHeader,
      });
      toast.success(`Category "${catName}" deleted`);
      await fetchCategories();
      setSelectedCategory('General');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete category');
    }
  };

  const selectedCatMeta = categories.find(c => c.name === selectedCategory);

  if (loadingCats) {
    return (
      <div style={{ minHeight: '100vh', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="animate-spin w-8 h-8 border-2 border-[#FF4F00] border-t-transparent rounded-full" />
      </div>
    );
  }

  const kbRightContent = (
    <>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
        background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        borderRadius: 4
      }}>
        <Coins style={{ width: 16, height: 16, color: '#F87217' }} />
        <span style={{ fontSize: 14, fontWeight: 500, color: textPrim }}>{user?.credits || 0}</span>
      </div>
      <Button
        variant="ghost"
        onClick={() => { logout(); navigate('/'); toast.success('Logged out'); }}
        className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100"
      >
        <LogOut className="w-4 h-4 mr-2" /> Logout
      </Button>
    </>
  );

  return (
    <div style={{ minHeight: '100vh', background: bg }}>
      <AppHeader rightContent={kbRightContent} />

      <FeatureGate feature="knowledge_base" featureLabel="Knowledge Base">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/dashboard')}
                  className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 -ml-2"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
                </Button>
              </div>
              <h1 className="text-3xl font-bold font-['Outfit'] flex items-center gap-3" style={{ color: textPrim }}>
                <Database className="w-8 h-8 text-[#FF4F00]" />
                Knowledge Base
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Upload and organise documents by category. Connect them to any agent during a chat for grounding.
              </p>
            </div>
          </div>

          <div className="flex gap-6">
            {/* ── Left sidebar: category list ─────────────────────────────────── */}
            <div className="w-64 flex-shrink-0">
              <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 4 }}>
                <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: textPrim }}>Categories</span>
                  <button
                    onClick={() => setShowNewCatInput(v => !v)}
                    className="text-slate-400 dark:text-slate-500 hover:text-[#FF4F00] dark:hover:text-[#FF4F00] transition-colors"
                    title="Add custom category"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                {/* New category input */}
                {showNewCatInput && (
                  <div style={{ padding: '8px 12px', borderBottom: `1px solid ${border}`, background: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
                    <div className="flex gap-1">
                      <input
                        autoFocus
                        value={newCatName}
                        onChange={e => setNewCatName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleCreateCategory(); if (e.key === 'Escape') { setShowNewCatInput(false); setNewCatName(''); } }}
                        placeholder="Category name…"
                        maxLength={50}
                        className="flex-1 text-xs border border-slate-200 dark:border-slate-600 rounded-sm px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-[#FF4F00] dark:focus:border-[#FF4F00]"
                      />
                      <button
                        onClick={handleCreateCategory}
                        disabled={creatingCat || !newCatName.trim()}
                        className="text-[#FF4F00] disabled:opacity-40 hover:text-[#CC3F00] transition-colors"
                      >
                        {creatingCat ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => { setShowNewCatInput(false); setNewCatName(''); }}
                        className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Category rows */}
                <div className="py-1">
                  {categories.map(cat => (
                    <div
                      key={cat.name}
                      className="group"
                      onClick={() => setSelectedCategory(cat.name)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 16px', cursor: 'pointer', transition: 'background 0.15s',
                        background: selectedCategory === cat.name
                          ? (isDark ? 'rgba(255,79,0,0.10)' : 'rgba(255,79,0,0.05)')
                          : 'transparent',
                        borderRight: selectedCategory === cat.name ? '2px solid #FF4F00' : '2px solid transparent',
                      }}
                      onMouseEnter={e => { if (selectedCategory !== cat.name) e.currentTarget.style.background = hoverBg; }}
                      onMouseLeave={e => { if (selectedCategory !== cat.name) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <FolderOpen style={{ width: 16, height: 16, flexShrink: 0, color: selectedCategory === cat.name ? '#FF4F00' : textMuted }} />
                        <span style={{ fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: selectedCategory === cat.name ? 500 : 400, color: selectedCategory === cat.name ? '#FF4F00' : textPrim }}>
                          {cat.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        {cat.file_count > 0 && (
                          <span style={{ fontSize: 12, padding: '2px 6px', borderRadius: 4, fontWeight: 500, border: '1px solid', ...getCategoryColor(cat.name, isDark) }}>
                            {cat.file_count}
                          </span>
                        )}
                        {!cat.is_predefined && (
                          <button
                            onClick={e => { e.stopPropagation(); handleDeleteCategory(cat.name); }}
                            className="opacity-0 group-hover:opacity-100 text-slate-300 dark:text-slate-600 hover:text-red-400 dark:hover:text-red-400 transition-all ml-1"
                            title={`Delete category "${cat.name}"`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* KB stats card */}
              <div style={{ marginTop: 16, background: cardBg, border: `1px solid ${border}`, borderRadius: 4, padding: 16 }}>
                <p style={{ fontSize: 11, fontWeight: 600, color: textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Summary</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                    <span style={{ color: textMuted }}>Total files</span>
                    <span style={{ fontWeight: 600, color: textPrim }}>{categories.reduce((s, c) => s + c.file_count, 0)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                    <span style={{ color: textMuted }}>Categories used</span>
                    <span style={{ fontWeight: 600, color: textPrim }}>{categories.filter(c => c.file_count > 0).length}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Right panel: file list + upload ─────────────────────────────── */}
            <div className="flex-1 min-w-0">
              {/* Upload zone */}
              <div
                style={{
                  background: isDragOver ? (isDark ? 'rgba(255,79,0,0.08)' : 'rgba(255,79,0,0.04)') : cardBg,
                  border: `2px dashed ${isDragOver ? '#FF4F00' : border}`,
                  borderRadius: 4, padding: 24, marginBottom: 16,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  cursor: uploading ? 'wait' : 'pointer', transition: 'all 0.2s',
                }}
                onClick={() => !uploading && fileInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.txt,.csv,.json,.md,.doc,.docx,.xls,.xlsx"
                  onChange={handleFileInputChange}
                />
                {uploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-8 h-8 text-[#FF4F00] animate-spin" />
                    <p style={{ fontSize: 14, color: textMuted }}>Uploading…</p>
                  </div>
                ) : (
                  <>
                    <Upload style={{ width: 32, height: 32, color: textMuted, marginBottom: 8 }} />
                    <p style={{ fontSize: 14, fontWeight: 500, color: textPrim }}>
                      Drop a file here or{' '}
                      <span style={{ color: '#FF4F00', textDecoration: 'underline' }}>click to upload</span>
                    </p>
                    <p style={{ fontSize: 12, color: textMuted, marginTop: 4 }}>
                      PDF, TXT, CSV, JSON, MD, DOC, DOCX, XLS, XLSX · Max 20 MB
                    </p>
                    <Badge style={{ marginTop: 12, border: '1px solid', fontSize: 12, fontWeight: 500, borderRadius: 4, ...getCategoryColor(selectedCategory, isDark) }}>
                      Uploading to: {selectedCategory}
                    </Badge>
                  </>
                )}
              </div>

              {/* Google Drive Import */}
              <div style={{ marginBottom: 16 }}>
                {!driveConnected ? (
                  <button
                    type="button"
                    onClick={connectDrive}
                    disabled={connectingDrive}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '10px 16px', border: `2px dashed ${isDark ? '#1e3a8a' : '#bfdbfe'}`,
                      borderRadius: 4, transition: 'all 0.2s', background: cardBg,
                      color: isDark ? '#94a3b8' : '#64748b', cursor: connectingDrive ? 'wait' : 'pointer',
                      opacity: connectingDrive ? 0.6 : 1
                    }}
                    onMouseEnter={e => { if (!connectingDrive) { e.currentTarget.style.borderColor = isDark ? '#2563eb' : '#60a5fa'; e.currentTarget.style.color = isDark ? '#60a5fa' : '#2563eb'; } }}
                    onMouseLeave={e => { if (!connectingDrive) { e.currentTarget.style.borderColor = isDark ? '#1e3a8a' : '#bfdbfe'; e.currentTarget.style.color = isDark ? '#94a3b8' : '#64748b'; } }}
                  >
                    {connectingDrive
                      ? <Loader2 style={{ width: 20, height: 20, color: '#3b82f6' }} className="animate-spin" />
                      : <img src="https://upload.wikimedia.org/wikipedia/commons/1/12/Google_Drive_icon_%282020%29.svg" alt="" style={{ width: 20, height: 20 }} />}
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{connectingDrive ? 'Connecting to Google Drive…' : 'Connect Google Drive to import files'}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowDrivePicker(v => !v)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '10px 16px', borderRadius: 4, transition: 'all 0.2s',
                      background: showDrivePicker ? (isDark ? 'rgba(59,130,246,0.2)' : '#eff6ff') : cardBg,
                      border: `2px dashed ${showDrivePicker ? (isDark ? '#2563eb' : '#60a5fa') : (isDark ? '#475569' : '#e2e8f0')}`,
                      color: showDrivePicker ? (isDark ? '#60a5fa' : '#1d4ed8') : (isDark ? '#94a3b8' : '#475569')
                    }}
                    onMouseEnter={e => {
                      if (!showDrivePicker) {
                        e.currentTarget.style.borderColor = isDark ? '#2563eb' : '#60a5fa';
                        e.currentTarget.style.color = isDark ? '#60a5fa' : '#2563eb';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!showDrivePicker) {
                        e.currentTarget.style.borderColor = isDark ? '#475569' : '#e2e8f0';
                        e.currentTarget.style.color = isDark ? '#94a3b8' : '#475569';
                      }
                    }}
                  >
                    <img src="https://upload.wikimedia.org/wikipedia/commons/1/12/Google_Drive_icon_%282020%29.svg" alt="" style={{ width: 20, height: 20 }} />
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{showDrivePicker ? 'Hide Drive files' : 'Import from Google Drive'}</span>
                  </button>
                )}

                {showDrivePicker && (
                  <div style={{ marginTop: 8, background: cardBg, border: `1px solid ${border}`, borderRadius: 4 }}>
                    <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}` }}>
                      <input
                        type="text"
                        placeholder="Search Drive files (PDF, Docs, Images)..."
                        value={driveSearch}
                        onChange={(e) => setDriveSearch(e.target.value)}
                        style={{
                          width: '100%', padding: '8px 12px', fontSize: 14, border: `1px solid ${border}`, borderRadius: 4,
                          background: inputBg, color: textPrim, transition: 'border-color 0.2s', outline: 'none'
                        }}
                        onFocus={e => e.currentTarget.style.borderColor = '#60a5fa'}
                        onBlur={e => e.currentTarget.style.borderColor = border}
                      />
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {driveLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                          <span className="ml-2 text-sm text-slate-400 dark:text-slate-500">Loading Drive files...</span>
                        </div>
                      ) : driveFiles.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8">
                          <FileText className="w-8 h-8 text-slate-200 dark:text-slate-600 mb-2" />
                          <p className="text-sm text-slate-400 dark:text-slate-500">No files found</p>
                          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Only PDF, Docs, PNG, JPEG files are shown</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-50 dark:divide-slate-700">
                          {driveFiles.map(file => (
                            <div
                              key={file.id}
                              className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                            >
                              <div className="w-8 h-8 rounded-sm bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                                {getDriveFileIcon(file.mime_type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{file.name}</p>
                                <p className="text-xs text-slate-400 dark:text-slate-500">
                                  {(file.mime_type || '').split('/').pop().split('.').pop()}
                                  {file.size && ` · ${formatBytes(file.size)}`}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => importDriveFile(file)}
                                disabled={driveImporting === file.id}
                                className="flex-shrink-0 px-3 py-1.5 rounded-sm text-xs font-semibold border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-50 transition-colors"
                              >
                                {driveImporting === file.id ? (
                                  <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Importing</span>
                                ) : 'Import'}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '8px 16px', borderTop: `1px solid ${border}`, background: isDark ? 'rgba(255,255,255,0.02)' : '#f8fafc' }}>
                      <p style={{ fontSize: 12, color: textMuted }}>
                        Files will be imported to <span style={{ fontWeight: 600, color: textPrim }}>{selectedCategory}</span> and indexed for retrieval
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Files panel */}
              <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 4 }}>
                <div style={{ padding: '16px 24px', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <FolderOpen style={{ width: 20, height: 20, color: '#FF4F00' }} />
                    <h2 style={{ fontWeight: 600, color: textPrim, margin: 0 }}>
                      {selectedCategory}
                      {selectedCatMeta && (
                        <span style={{ color: textMuted, fontWeight: 400, fontSize: 14, marginLeft: 8 }}>
                          ({selectedCatMeta.file_count} {selectedCatMeta.file_count === 1 ? 'file' : 'files'})
                        </span>
                      )}
                    </h2>
                  </div>
                  <button
                    onClick={() => fetchFiles(selectedCategory)}
                    className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                    title="Refresh"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>

                {loadingFiles ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400 dark:text-slate-500" />
                  </div>
                ) : files.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <FileText className="w-12 h-12 text-slate-200 dark:text-slate-600 mb-4" />
                    <p className="text-slate-500 dark:text-slate-400 font-medium">No files in {selectedCategory}</p>
                    <p className="text-slate-400 dark:text-slate-500 text-sm mt-1">
                      Upload a file above to add it to this category.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 dark:divide-slate-700">
                    {files.map(file => (
                      <div
                        key={file.id}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', transition: 'background 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = hoverBg; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className={`w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0 border ${getCategoryColor(file.category)}`}>
                            <FileText className="w-4 h-4" />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <p style={{ fontSize: 14, fontWeight: 500, color: textPrim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>
                              {file.original_filename}
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 2 }}>
                              <span style={{ fontSize: 12, color: textMuted }}>{formatBytes(file.file_size)}</span>
                              <span style={{ fontSize: 12, color: isDark ? '#3d4f7a' : '#cbd5e1' }}>·</span>
                              <span style={{ fontSize: 12, color: textMuted }}>{new Date(file.indexed_at).toLocaleDateString()}</span>
                              <span style={{ fontSize: 12, color: isDark ? '#3d4f7a' : '#cbd5e1' }}>·</span>
                              <StatusBadge status={file.status} chunkCount={file.chunk_count} />
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteFile(file.id, file.original_filename)}
                          disabled={deletingFileId === file.id}
                          className="ml-4 text-slate-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400 transition-colors flex-shrink-0"
                          title="Remove from knowledge base"
                        >
                          {deletingFileId === file.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Help card */}
              <div style={{ marginTop: 16, background: isDark ? 'rgba(255,79,0,0.08)' : 'rgba(255,79,0,0.05)', border: `1px solid ${isDark ? 'rgba(255,79,0,0.25)' : 'rgba(255,79,0,0.18)'}`, borderRadius: 4, padding: 16 }}>
                <p style={{ fontSize: 14, fontWeight: 500, color: '#FF4F00', marginBottom: 4 }}>
                  How to use your knowledge base in a chat
                </p>
                <p style={{ fontSize: 12, color: textPrim, opacity: isDark ? 0.7 : 0.8 }}>
                  Open any agent, click the{' '}
                  <span className="font-semibold">Connect KB</span> button (
                  <Database className="w-3 h-3 inline" />) in the chat toolbar, and select a
                  category. The agent will automatically retrieve the most relevant chunks from
                  your documents and use them as context for your conversation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </FeatureGate>
    </div>
  );
}